import React from "react";
import { isEmpty } from "lodash";
import { Row, Col, Card } from "reactstrap";
import readXlsxFile from "read-excel-file";
import PageLayout from "../../components/PageLayout";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { drop } from "lodash";
import ValidationSummary from "../../components/ValidationSummary";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { getGridDateTemplate } from "../../utils/content";
import { getSimCards, importSimCards } from "../../api/simcards";
import { it } from "../../utils/content";

const ImportSimcardsView = () => {
  const { domains, geo, onSuccess, onError } = useAuth0();
  const [loading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    form: {},
    errors: {},
    rows: []
  });

  const validate = formData => {
    const errors = {};
    if (
      !formData.file ||
      formData.file.type !==
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    )
      errors.file = "selezionare un file excel";
    if (!formData.carrier) errors.carrier = `Il campo operatore è obbligatorio`;
    if (!formData.insertDate)
      errors.insertDate = `La data di inserimento è obbligatoria`;
    if (!formData.type) errors.type = `Selezionare il tipo di SIM`;
    if (
      !formData.lines ||
      (formData.lines &&
        formData.lines.length &&
        formData.lines.filter(row => !row[0] || !row[1]).length)
    ) {
      errors.file = `Il file selezionato non è valido: per alcuni record non sono presenti Telefono o ICCID`;
    }

    return errors;
  };

  const onResetForm = () => {
    setState({
      form: {},
      errors: {},
      rows: []
    });

    document.getElementById("simcardsFileInput").value = "";
  };

  const onFieldChange = changedField => {
    const form = { ...state.form, ...changedField };
    const errors = validate(form);
    const rows = (form.lines || []).map(item => {
      const exist = form.existing.find(el => el.iccid === item[0]);
      return exist
        ? { ...exist, existing: true }
        : {
          iccid: String(item[0]),
          number: String(item[1]),
          createdat: form.insertDate,
          type: form.type,
          carrier: form.carrier
        };
    });
    setState({ form, errors, rows });
  };

  const handleFileChange = async (e, onFieldChange) => {
    try {
      setLoading(true);
      const files = e.target.files;
      const toImport = await readXlsxFile(files[0]);
      const lines = drop(toImport, 1);
      const existing = await getSimCards(lines.map(el => el[0]));
      onFieldChange({ file: files[0], lines, existing }, true);
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  };

  const carrierChange = (e, onFieldChange) => {
    onFieldChange({ carrier: e.value });
  };

  const simTypeChange = (e, onFieldChange) => {
    onFieldChange({ type: e.value });
  };

  const changeImportDate = (e, onFieldChange) => {
    onFieldChange({ insertDate: e.value });
  };

  const runImport = async () => {
    const cards = state.rows.filter(el => !el.existing);
    if (!cards || !cards.length) return;
    try {
      setLoading(true);
      const result = await importSimCards(cards);
      onResetForm();
      onSuccess();
    } catch (error) {
      onError();
    } finally {
      setLoading(false);
    }
  };

  const renderRowsToImport = rows => {
    return (
      <DataTable
        value={rows}
        autoLayout={false}
        className="p-datatable-hoverable-rows p-datatable-customers"
        emptyMessage={() => (<div className="text-center">Nessun dato in tabella</div>)}
        rowClassName={rowData => {
          return { "p-highlight": rowData.existing };
        }}
      >
        <Column field="number" header="Telefono" filter sortable />
        <Column field="iccid" header="ICCID" filter sortable />
        <Column field="carrier" header="Operatore" body={(el) => el.type ? domains.carriers.find(x => x.value === el.carrier).label : null} />
        <Column field="type" header="Tipo" body={(el) => el.type ? domains.simTypes.find(x => x.value === el.type).label : null} />
        <Column
          field="createdat"
          body={getGridDateTemplate}
          header="Creato il"
          filter
          sortable
        />
      </DataTable>
    );
  };

  const renderImportMessage = (rows) => {
    const total = rows.length;
    const existing = rows.filter(el => el.existing).length;
    const newRecords = total - existing;

    return `Il file selezionato contiene ${total} SIM di cui ${existing} già importate in precedenza (evidenziate in blu) e ${newRecords} nuove.`;
  }

  return (
    <PageLayout
      title="Importazione SIM"
      optionalNav={{ link: "/backoffice/simcards", text: "SIM" }}
    >
      <Row>
        <Col>
          <ValidationSummary errors={state.errors} />
        </Col>
      </Row>
      <Row>
        <Col sm="6" xs="12">
          <strong className="mt-2">Operatore</strong>
          <Dropdown
            filter
            filterBy="label"
            className={state.errors.carrier ? "p-error" : ""}
            showClear={true}
            style={{ width: "100%" }}
            value={state.form.carrier || ""}
            options={domains.carriers}
            onChange={e => {
              carrierChange(e, onFieldChange);
            }}
          />
        </Col>
        <Col sm="6" xs="12">
          <strong className="mt-2">Tipo SIM</strong>
          <Dropdown
            filter
            filterBy="label"
            className={state.errors.type ? "p-error" : ""}
            showClear={true}
            style={{ width: "100%" }}
            value={state.form.type || ""}
            options={domains.simTypes}
            onChange={e => {
              simTypeChange(e, onFieldChange);
            }}
          />
        </Col>
        <Col sm="6" xs="12">
          <div><strong className="mt-2">Data Inserimento</strong></div>
          <Calendar
            locale={it}
            monthNavigator={true}
            yearNavigator={true}
            showButtonBar={true}
            maxDate={new Date()}
            className={state.errors.model ? "p-error" : ""}
            yearRange="1900:2099"
            value={state.form.insertDate || ""}
            onChange={e => {
              changeImportDate(e, onFieldChange);
            }}
            dateFormat="dd/mm/yy"
            showIcon={true}
          />
        </Col>
        <Col sm="6" xs="12">
          <div><strong className="mt-2">File di import</strong></div>
          <span
            className={`p-button p-fileupload-choose p-component p-button-text-icon-left ${
              state.errors.file ? "p-error" : ""
              }`}
          >
            <span className="p-button-icon-left pi pi-plus" />
            <span className="p-button-text p-clickable">
              {state.form.file ? state.form.file.name : "Seleziona file"}
            </span>
            <input
              type="file"
              id="simcardsFileInput"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "inline" }}
              onChange={e => {
                handleFileChange(e, onFieldChange);
              }}
            />
          </span>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="float-right mt-5">
            <Button
              className="p-button-warning mr-2 mb-2"
              disabled={loading}
              label="Reset"
              icon="pi pi-times"
              onClick={onResetForm}
            />
            <Button
              disabled={loading || !isEmpty(state.errors)}
              className="p-button-success"
              label="Importa"
              icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
              onClick={runImport}
            />
          </div>
        </Col>
      </Row>
      {state.rows.length > 0 && (
        <Row>
          <Col>
            <strong>
              {renderImportMessage(state.rows)}
            </strong>
            {renderRowsToImport(state.rows)}
          </Col>
        </Row>
      )}
    </PageLayout>
  );
};

export default ImportSimcardsView;
